import {CatalogItemVO, EnumTypVysledek, SearchResultVO} from '../api';

/**
 * Počet zobrazených výsledků na skupinu (vrstva / adresy / parcely / ulice) – MCW-29537.
 * Stejnou hodnotu má server v {@code SearchServiceImpl.LAYERS_FEATURE_LIMIT}; 0 a méně = bez omezení.
 */
export const SEARCH_GROUP_RESULT_LIMIT = 10;

/** Název skupiny pro výsledky typu „aktivace vrstvy“ (výsledek bez featureId). */
const LAYER_ACTIVATION_GROUP = 'Vrstvy';
/** Záložní název skupiny objektů, pokud vrstvu nelze zařadit do katalogu. */
const DEFAULT_POI_GROUP = 'Objekty';

/**
 * Relevance výsledku. Skóre počítá server (cz.marbes.mapio.utils.SearchScore) a posílá ho v poli `weight`
 * znormalizované do intervalu (0, 1> – klient ho jen čte, aby existovalo jediné místo, kde se relevance určuje.
 */
export const getSearchResultScore = (result: SearchResultVO): number => Number(result.weight ?? 0);

/**
 * Mapa layerId → název hlavní (nejvyšší) skupiny katalogu, do které vrstva patří.
 * Slouží ke sloučení výsledků z dílčích vrstev pod jednu nadřazenou skupinu.
 */
export const buildLayerGroupNameMap = (catalog?: CatalogItemVO[]): Map<number, string> => {
    const result = new Map<number, string>();
    (catalog ?? []).forEach((rootItem: CatalogItemVO): void => {
        const groupName: string = rootItem.label;
        const collect = (item: CatalogItemVO): void => {
            if (item.layerId != null) result.set(item.layerId, groupName);
            (item.children ?? []).forEach(collect);
        };
        collect(rootItem);
    });
    return result;
};

/** Vstupní skupina výsledků tak, jak dorazila ze serveru (klíč = layerId nebo název lokační skupiny). */
export type SearchResultGroup<T extends SearchResultVO> = {
    /** Klíč z odpovědi – layerId (číslo v textu) nebo název lokační skupiny (adresy, ulice…). */
    key: string;
    /** Název vrstvy / lokační skupiny z layerInfo. */
    name?: string;
    typVysledek?: EnumTypVysledek;
    items: T[];
};

export type BuildLegacySearchItemsOptions = {
    /** Lokace (adresy, ulice, parcely) zobrazit před objekty. */
    locationsFirst?: boolean;
};

export type BuildSearchItemsOptions = {
    /** Lokace (adresy, ulice, parcely) zobrazit před objekty. */
    locationsFirst?: boolean;
    /** Název nadřazené skupiny katalogu pro danou vrstvu. */
    layerGroupNames?: Map<number, string>;
    /** Maximální počet výsledků na skupinu; 0 a méně = bez omezení, neuvedeno = výchozí hodnota. */
    limit?: number;
};

export type BuildSearchItemsResult<T extends SearchResultVO> = {
    /** Plochý seznam – nadpis skupiny ({title}) následovaný jejími výsledky. */
    items: (T & {title?: string})[];
    /** Celkový počet nalezených výsledků (před omezením na limit). */
    foundCount: number;
    /** ID vrstev, ve kterých byly nalezeny objekty (pro legendu). */
    layerIds: number[];
};

/**
 * Původní (před MCW-29537) sestavení seznamu výsledků: skupiny v pořadí, v jakém dorazily ze serveru,
 * uvnitř skupiny beze změny pořadí a bez omezení počtu. Používá se pro organizace BEZ příznaku
 * „Atlas přístupnosti“ (MCW-29581) – jejich hledání musí zůstat přesně takové, jaké bylo.
 */
export const buildLegacySearchItems = <T extends SearchResultVO & {typVysledek?: EnumTypVysledek; title?: string}>(
    groups: SearchResultGroup<T>[],
    {locationsFirst}: BuildLegacySearchItemsOptions,
): BuildSearchItemsResult<T> => {
    const poiItems: (T & {title?: string})[] = [];
    const locationItems: (T & {title?: string})[] = [];
    const layerIds = new Set<number>();

    groups.forEach((group: SearchResultGroup<T>): void => {
        const isLocation: boolean = group.typVysledek === EnumTypVysledek.LOCATION;
        if (!isLocation) {
            // Klíče mohou být i nečíselné (ne-vrstvové výsledky, např. adresy) – ty do ID vrstev nepatří.
            const layerIdNum: number = Number(group.key);
            if (!Number.isNaN(layerIdNum)) layerIds.add(layerIdNum);
        }
        (isLocation ? locationItems : poiItems).push({title: group.name} as T & {title?: string}, ...group.items);
    });

    const items = locationsFirst ? [...locationItems, ...poiItems] : [...poiItems, ...locationItems];
    return {items, foundCount: items.filter(item => !item.title).length, layerIds: Array.from(layerIds)};
};

type Bucket<T extends SearchResultVO> = {
    name: string;
    items: {item: T; score: number}[];
    /** Pořadí prvního výskytu – zachovává pořadí lokačních skupin z odpovědi. */
    order: number;
};

/**
 * Sestaví výsledný seznam našeptávače (MCW-29537):
 * - objekty z dílčích vrstev sloučí do jedné nadřazené skupiny katalogu,
 * - v rámci skupiny řadí podle relevance (skóre) spočítané serverem,
 * - každou skupinu omezí na `limit` výsledků (nadpis pak nese „zobrazeno X z Y“),
 * - objekty jsou před lokacemi (adresy, ulice, parcely), pokud není `locationsFirst`.
 */
export const buildSearchItems = <T extends SearchResultVO & {typVysledek?: EnumTypVysledek; title?: string}>(
    groups: SearchResultGroup<T>[],
    {locationsFirst, layerGroupNames, limit}: BuildSearchItemsOptions,
): BuildSearchItemsResult<T> => {
    // undefined = hodnota z konfigurace nedorazila → výchozích 10; 0 a méně = bez omezení.
    const platnyLimit: number = limit ?? SEARCH_GROUP_RESULT_LIMIT;
    const poiBuckets = new Map<string, Bucket<T>>();
    const layerBuckets = new Map<string, Bucket<T>>();
    const locationBuckets = new Map<string, Bucket<T>>();
    const layerIds = new Set<number>();
    let order = 0;

    const addToBucket = (buckets: Map<string, Bucket<T>>, name: string, item: T, score: number): void => {
        let bucket: Bucket<T> = buckets.get(name);
        if (!bucket) {
            bucket = {name, items: [], order: order++};
            buckets.set(name, bucket);
        }
        bucket.items.push({item, score});
    };

    groups.forEach((group: SearchResultGroup<T>): void => {
        const layerIdNum: number = Number(group.key);
        const hasLayerId: boolean = !Number.isNaN(layerIdNum);
        const groupName: string = (hasLayerId ? layerGroupNames?.get(layerIdNum) : undefined) ?? group.name ?? DEFAULT_POI_GROUP;

        group.items.forEach((item: T): void => {
            const score: number = getSearchResultScore(item);
            if (group.typVysledek === EnumTypVysledek.LOCATION) {
                addToBucket(locationBuckets, group.name ?? DEFAULT_POI_GROUP, item, score);
            } else if (item.attrs?.featureId) {
                if (hasLayerId) layerIds.add(layerIdNum);
                addToBucket(poiBuckets, groupName, item, score);
            } else if (layerGroupNames) {
                // Ve sloučeném režimu (zjednodušené public zobrazení) dáváme nabídky zapnutí vrstvy stranou,
                // aby se nemíchaly mezi objekty sloučené nadřazené skupiny.
                addToBucket(layerBuckets, LAYER_ACTIVATION_GROUP, item, score);
            } else {
                // Běžný našeptávač: nabídka zapnutí vrstvy zůstává ve skupině své vrstvy/kategorie (MCW-26078).
                if (hasLayerId) layerIds.add(layerIdNum);
                addToBucket(poiBuckets, groupName, item, score);
            }
        });
    });

    let foundCount = 0;
    const renderBuckets = (buckets: Map<string, Bucket<T>>, sortByScore: boolean): (T & {title?: string})[] => {
        const list: Bucket<T>[] = Array.from(buckets.values());
        if (sortByScore) {
            // Skupiny s relevantnějším nejlepším výsledkem jdou nahoru.
            const bestScore = (bucket: Bucket<T>): number => bucket.items.reduce((best, i) => Math.max(best, i.score), 0);
            list.sort((a, b) => bestScore(b) - bestScore(a) || a.order - b.order);
        } else {
            list.sort((a, b) => a.order - b.order);
        }
        return list.flatMap((bucket: Bucket<T>): (T & {title?: string})[] => {
            const sorted: T[] = [...bucket.items].sort((a, b) => b.score - a.score).map(i => i.item);
            foundCount += sorted.length;
            // limit <= 0 znamená „bez omezení“ (panel hledání zobrazuje celý seznam – MCW-28421).
            const shown: T[] = platnyLimit > 0 ? sorted.slice(0, platnyLimit) : sorted;
            const title: string = sorted.length > shown.length ? `${bucket.name} (zobrazeno ${shown.length} z ${sorted.length})` : bucket.name;
            return [{title} as T & {title?: string}, ...shown];
        });
    };

    const poiItems = [...renderBuckets(poiBuckets, true), ...renderBuckets(layerBuckets, false)];
    const locationItems = renderBuckets(locationBuckets, false);

    return {
        items: locationsFirst ? [...locationItems, ...poiItems] : [...poiItems, ...locationItems],
        foundCount,
        layerIds: Array.from(layerIds),
    };
};
